import type { SalaryFormat } from '@app/api/ClientServices';
import type { NLeavePageProvider } from '@app/providers/LeavePageProvider/LeavePageProvider.types';
import type {
  NAnalytics,
  NClientServices,
  NLegalEntities,
  NUsers,
} from '@app/types';
import type { UserAccessRightsInterface } from '@app/types/NewAccessRight';

import type { ServicesLoadState } from '@pages/Employees/EditEmployee/data/copyingServices';

/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: здесь типы Any планируется убрать отдельным этапом в рамках task/3444,
// т.к. у нас завязано 62 места в кодовой базе только на setNewData,
// не считая других переменных из LeavePageProvider

export namespace NEmployees {
  export type HandleUnsavedEmployeeInfoTab = () => Promise<boolean>;

  export type Employee = {
    Id: number;
    NameFirst: string;
    NameLast: string;
    Position: string;
    PositionId: number;
    IsDeleted: boolean;
    IsTerminated: boolean;
    CountClientServices: number;
    WorkScheduleTill: null | string;
    IsBelongToCurrentBranch: boolean;
  };

  export type EmployeeForSelector = {
    Gender: string | null;
    Id: number;
    IsDeleted: boolean;
    IsTerminated: boolean;
    NameFirst: string | null;
    NameLast: string | null;
    Photo: string | null;
    Position: string | null;
    PositionId: number | null;
  };

  export type ModelClientServiceEmployee = {
    Id: number;
    UserId: number;
    ServiceId: number;
    Duration: number;
    SalaryFormat: string;
    SalaryAmount: number;
  };

  export type ActiveEmployee = NUsers.User | undefined;

  export type EditEmployeeInfoTabProps = {
    employee: ActiveEmployee;
    infoData: ActiveEmployee;
    setInfoData: (value: NUsers.User) => void;
    setNewData: NLeavePageProvider.SetData;
    newData: NLeavePageProvider.Data;
    allServices: ModelClientService[];
    setAllServices: (value: ModelClientService[]) => void;
    hasUserRightToEdit: boolean;
    allSettings: ModelUserSettings | undefined;
    setEmployee: (data: EditEmployeeInfoTabProps['employee']) => void;
    hasUserRightToViewAccessRightTab: boolean;
    isNeedToUpdateUserPreview: boolean;
    setIsNeedToUpdateUserPreview: (args: boolean) => void;
    hasUserRightToAttachServices: boolean;
    editableUserAccessRights: UserAccessRightsInterface | null;
    editableUserUnsavedAccessRights: UserAccessRightsInterface | null;
    setEditableUserUnsavedAccessRights: React.Dispatch<
      React.SetStateAction<UserAccessRightsInterface | null>
    >;
    isGetUserAccessRightsLoading: boolean;
    setEditableUserAccessRights: React.Dispatch<
      React.SetStateAction<UserAccessRightsInterface | null>
    >;
    hasCurrentUserRightToViewAllRecords: boolean | undefined;
    getEmployeeData: () => Promise<void>;
    servicesLoadState: ServicesLoadState;
    setHandleUnsavedChanges: (
      handler: HandleUnsavedEmployeeInfoTab | null
    ) => void;
  };

  export type EmployeeInfoTabProps = {
    employee: ActiveEmployee;
    newData: NLeavePageProvider.Data;
    setNewData: NLeavePageProvider.SetData;
    setEmployee: (data: ActiveEmployee) => void;
    setIsNeedToUpdateUserPreview: (args: boolean) => void;
    setHandleUnsavedChanges: (
      handler: HandleUnsavedEmployeeInfoTab | null
    ) => void;
  };

  export type EmployeeAdditionalTabProps = {
    employee: EditEmployeeInfoTabProps['employee'];
    setNewData: NLeavePageProvider.SetData;
    newData: NLeavePageProvider.Data;
    setEmployee: (data: EditEmployeeInfoTabProps['employee']) => void;
    setIsNeedToUpdateUserPreview: (args: boolean) => void;
    setHandleUnsavedChanges: (
      handler: HandleUnsavedEmployeeInfoTab | null
    ) => void;
  };

  export type EmployeeSettingsTab = {
    employee: ActiveEmployee;
    allSettings: ModelUserSettings | undefined;
    hasCurrentUserRightToViewAllRecords?: boolean;
  };

  export type EmployeeClientServicesTab = {
    employee: ActiveEmployee;
    allServices: ModelClientService[];
    setAllServices: (value: ModelClientService[]) => void;
    setNewData: NLeavePageProvider.SetData;
    getEmployeeData: () => Promise<void>;
    servicesLoadState: ServicesLoadState;
  };

  export interface ModelClientService {
    Id: number;
    Name: string | null;
    PriceS: number;
    PriceE: number | null;
    Duration: number;
    ServiceType: NClientServices.ServiceType;
    CategoryId: number | null;
    Category: string | null;
    BranchId: number;
    IsDeleted: boolean;
    CountEmployees: number;
    ClientServiceAdditions: ModelClientServiceAddition[] | null;
    PublicName?: string | null;
    SalaryFormat: SalaryFormat;
    SalaryAmount: number;
    Description: string | null;
    MedicalServiceCode?: string | null;
  }

  export interface ModelClientServiceWithOptionalProps
    extends ModelClientService {
    UserId?: number | null;
    isAddingFromModal?: boolean;
    isDeletedAtClientSide?: boolean;
    isDurationChanged?: boolean;
  }

  export type ModelUserSettings = {
    UserId: number;
    IsSkipRecordNotifications: boolean;
    IsRecordStatusChanges: boolean;
    IsAllRecordsStatusChanges: boolean;
  };

  export type ModelClientServiceAddition = {
    Id: number;
    ServiceId: number;
    AdditionServiceId: number;
    Name: string | null;
  };

  export type ModelEmployeeOrder = {
    EmployeeId: number;
    Employee: string | null;
    PositionId: number | null;
    Position: string | null;
    OrderIndex: number;
  };

  export type ModelEmployeeOrderForFront = ModelEmployeeOrder & {
    Key: string;
  };

  export type SortEmployeesSavingButtonProps = {
    saveEmployees: () => void;
    isSaving: boolean;
  };

  export type FinishModal = (values: {
    Name: string;
    Description: string;
  }) => void;

  export type CreateLegalEntity = (
    values: NLegalEntities.AddLegalEntityFormProps
  ) => void;

  export type ModelEmployeeSearchFilter = {
    SearchTerm: string | null;
    BranchIds: number[] | null;
    PositionIds: number[] | null;
    IsDeleted: boolean | null;
    IsTerminated: boolean | null;
    UsersPerPage: number;
  };

  export type ModelEmployeeSearchResult = {
    Id: number;
    Filter: ModelEmployeeSearchFilter;
    Employees: NAnalytics.ModelEmployeeLite[];
    PageN: number;
    CountPages: number;
    CountEmployees: number;
  };

  export type ModelBranchEmployeesOrder = {
    BranchId: number;
    Branch: string | null;
    Employees: ModelEmployeeOrder[] | null;
  };

  export type ModelOrderIndex = {
    ObjectId: number;
    OrderIndex: number;
  };

  export type ModelEmployee = {
    Id: number;
    NameFirst: string | null;
    NameLast: string | null;
    NameMiddle: string | null;
    NameFull: string | null;
    Gender: string | null;
    PositionId: number | null;
    Position: string | null;
    IsDeleted: boolean;
    IsTerminated: boolean;
    Photo: string | null;
    CountClientServices: number;
  };
}
